import { type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

/**
 * Component state held in the React Query cache instead of `useState`.
 *
 * Why: the state survives unmounting (switching dual-panel tabs, conversations or
 * dashboards), it is scoped by its query key, and it can opt into localStorage via
 * `meta.shouldPersist` like any other cached entry. The query never fetches
 * (`enabled: false`) — the cache is only a store.
 *
 * Reads and updates always see `{ ...initialData, ...stored }`, so a partially stored
 * value (an older persisted shape) still exposes every field. `key` and `initialData`
 * must be stable across renders (a memoized key, a module-level default).
 */
export const useCachedState = <T extends object>(
  key: QueryKey,
  initialData: T,
  meta?: Record<string, unknown>,
): [T, (recipe: (prev: T) => T) => void, () => T] => {
  const queryClient = useQueryClient();

  const { data } = useQuery<T>({
    queryKey: key,
    enabled: false,
    initialData,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    meta,
  });

  const update = useCallback(
    (recipe: (prev: T) => T) => queryClient.setQueryData<T>(key, (prev) => recipe({ ...initialData, ...prev })),
    [queryClient, key, initialData],
  );

  // Memoized on the cached value, so consumers keep a stable object between updates.
  const state = useMemo(() => ({ ...initialData, ...data }), [initialData, data]);

  /** The stored value as of *now* — `state` only catches up on the next render. */
  const read = useCallback(
    () => ({ ...initialData, ...queryClient.getQueryData<T>(key) }),
    [queryClient, key, initialData],
  );

  return [state, update, read];
};
